import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Text as NativeText,
    ScrollView,
    View
} from "react-native";
import type ITraceCodeBlockProps from "./type";
import type {
    TraceCodeBlockCopyStatus
} from "./type";
import stylesheet, {
    TRACE_CODE_MAX_LINES,
    TRACE_CODE_LANGUAGE,
    useStyles
} from "./stylesheet";
import {
    TraceCodeBlockContext
} from "./context";
import {
    TraceCodeBlockCopyIcon
} from "./icons";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getCodeCopyStatusDelay,
    getCodeTokenColor
} from "../../../codeViewer/stylesheet";
import {
    splitTokensIntoLines,
    parseCode
} from "../../../codeViewer/parser";
import {
    concentric
} from "../../../../helpers/theme/concentric";
import {
    copyTextToClipboard
} from "../../../../utils";
import Button from "../../../button";
import Text from "../../../text";

const TraceCodeBlock: FC<ITraceCodeBlockProps> = ({
    maxLines = TRACE_CODE_MAX_LINES,
    customLocalize,
    customTheme,
    copyLabel,
    label,
    style,
    text
}) => {
    const {
        controlPaddings,
        shapeConfigs,
        typography,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        copyStatus,
        setCopyStatus
    ] = useState<TraceCodeBlockCopyStatus>("idle");

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isMountedRef = useRef(true);

    const {
        container: containerDynamicStyle,
        body: bodyDynamicStyle,
        code: codeDynamicStyle,
        head: headDynamicStyle,
        more: moreDynamicStyle
    } = useStyles({
        controlPaddings,
        shapeConfigs,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    });

    const isCopied = copyStatus === "copied";

    const codeBlockContext = useMemo(() => ({
        customTheme,
        isCopied
    }), [
        customTheme,
        isCopied
    ]);

    const lines = useMemo(() => splitTokensIntoLines(parseCode(text, TRACE_CODE_LANGUAGE)), [
        text
    ]);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;

            if(timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const handleCopy = async () => {
        const isCopied = await copyTextToClipboard(text);

        if(!isMountedRef.current) {
            return;
        }

        if(timerRef.current) {
            clearTimeout(timerRef.current);
        }

        setCopyStatus(isCopied ? "copied" : "failed");

        timerRef.current = setTimeout(() => {
            timerRef.current = null;

            setCopyStatus("idle");
        }, getCodeCopyStatusDelay({
            durations
        }));
    };

    const renderLine = (lineTokens: ReturnType<typeof splitTokensIntoLines>[number], lineIndex: number) => {
        return <NativeText
            style={[
                stylesheet.code,
                codeDynamicStyle
            ]}
            key={lineIndex}
        >
            {
                lineTokens.length ?
                    lineTokens.map((token, tokenIndex) => <NativeText
                        style={{
                            color: getCodeTokenColor({
                                type: token.type,
                                colors
                            })
                        }}
                        key={tokenIndex}
                    >
                        {token.value}
                    </NativeText>)
                :
                    " "
            }
        </NativeText>;
    };

    const renderMore = () => {
        const hiddenCount = lines.length - maxLines;

        if(hiddenCount <= 0) {
            return null;
        }

        return <View
            style={moreDynamicStyle}
        >
            <Text
                customTheme={customTheme}
                variant="bodySmallSize"
                color="low"
            >
                {localize("x-more-lines", [
                    hiddenCount
                ])}
            </Text>
        </View>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-label={label}
        role="group"
    >
        <View
            style={[
                stylesheet.head,
                headDynamicStyle
            ]}
        >
            <Text
                customTheme={customTheme}
                variant="labelSmallSize"
                numberOfLines={1}
                color="mid"
            >
                {label}
            </Text>
            <TraceCodeBlockContext.Provider
                value={codeBlockContext}
            >
                <Button
                    onPress={() => {
                        handleCopy().catch(() => undefined);
                    }}
                    accessibilityLabel={copyLabel}
                    icon={TraceCodeBlockCopyIcon}
                    borderRadius={concentric({
                        inset: spaces.spacingXs,
                        role: radiuses.actions,
                        outer: radiuses.form
                    })}
                    customTheme={customTheme}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </TraceCodeBlockContext.Provider>
        </View>
        <ScrollView
            showsHorizontalScrollIndicator={false}
            horizontal={true}
        >
            <View
                style={[
                    stylesheet.body,
                    bodyDynamicStyle
                ]}
            >
                {lines.slice(0, maxLines).map(renderLine)}
            </View>
        </ScrollView>
        {renderMore()}
    </View>;
};
export default TraceCodeBlock;
